/**
 * Save a report the server sent back, and say something when it did not.
 *
 * The blob dance was written out by hand at every call site, and every copy
 * had the same hole: when the server answers with JSON instead of a
 * spreadsheet -- no rows matched, the selection is too large, the session
 * expired -- the browser saves that JSON as an .xlsx. The person opens a
 * corrupt file and has no idea why.
 *
 * A fetch of a Blob cannot fail on status alone, because RTK Query hands back
 * whatever came. So the type is checked here: anything that is not a
 * spreadsheet is read as text, and its message is what the caller shows.
 */

const EXCEL_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export type DownloadResult = { ok: true } | { ok: false; message: string };

export const downloadReport = async (
  blob: Blob,
  fileName: string,
): Promise<DownloadResult> => {
  if (!blob) {
    return { ok: false, message: "The export returned nothing." };
  }

  // An error came back in place of the file.
  if (blob.type && blob.type !== EXCEL_TYPE) {
    let message = "The export could not be produced.";
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.error || message;
    } catch {
      // Not JSON either. Keep the generic message rather than showing the
      // person a wall of raw response.
    }
    return { ok: false, message };
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { ok: true };
};

/** `sales-report-2026-09-29.xlsx` — matches what the server names it. */
export const datedFileName = (base: string) =>
  `${base}-${new Date().toISOString().slice(0, 10)}.xlsx`;

export default downloadReport;
